import { Inject, Injectable } from '@angular/core';
import { AtsDate } from '@ats/utility';
import { Observable, ReplaySubject, filter, map, shareReplay } from 'rxjs';
import { STORAGE_DOMAIN } from './storage-domain.token';
import { CMStorageEvent } from './storage-event.type';

@Injectable({
	providedIn: 'root',
})
export class StorageService {
	private readonly storageEvent = new ReplaySubject<CMStorageEvent>();
	readonly events$: Observable<any> = this.storageEvent.asObservable().pipe(
		shareReplay(1),
		map(ev => (ev.data !== null ? ev.data : null))
	);

	constructor(@Inject(STORAGE_DOMAIN) private readonly domain: string) {
		window.addEventListener('storage', e => this.handleStorageEvent(e));
	}

	public getStorageItemEvents$<T = any>(name: string, type?: 'DELETE' | 'SAVE') {
		return this.storageEvent.asObservable().pipe(
			shareReplay(1),
			filter(ev => ev.key === name && (!type || (type === 'DELETE' && !!ev.data))),
			map(ev => ev.data as T)
		);
	}

	public get<T = any>(name: string): T | null {
		try {
			const dataStr = localStorage.getItem(`${this.domain}-${name}`);
			if (dataStr) {
				const data = JSON.parse(dataStr);
				if (AtsDate.isPast((data || {}).expiry || new Date('1985-07-03'))) {
					throw new Error();
				}
				return data.value;
			}
			throw new Error();
		} catch (ex) {
			this.delete(name);
			return null;
		}
	}

	public delete(name: string): void {
		const { key, timestampKey } = this.getKeys(name);
		localStorage.removeItem(key);
		localStorage.removeItem(timestampKey);
		this.emitStorageEvent<'DELETE'>({ key: name });
	}

	public save(name: string, value: any, expiry: number = 40): void {
		const { key, timestampKey } = this.getKeys(name);
		localStorage.setItem(
			key,
			JSON.stringify({
				value,
				expiry: AtsDate.addMinutes(expiry, new Date()),
			})
		);
		localStorage.setItem(timestampKey, JSON.stringify(new Date().toISOString()));
		this.emitStorageEvent<'SAVE'>({
			key: name,
			data: value,
		});
	}

	private getKeys(name: string): { key: string; timestampKey: string } {
		return {
			key: `${this.domain}-${name}`,
			timestampKey: `${this.domain}-${name}-LastUpdate`,
		};
	}

	public emitStorageEvent<T extends 'SAVE' | 'DELETE'>(
		event: T extends 'SAVE' ? Required<CMStorageEvent> : CMStorageEvent
	) {
		this.storageEvent.next(event);
	}

	private handleStorageEvent = (event: StorageEvent) => {
		if (!event.key || !event.key.includes('LastUpdate')) {
			return;
		}

		const key = event.key.replace(`${this.domain}-`, '').replace('-LastUpdate', '');
		const newValue = this.get(key);

		if (newValue === null) {
			this.emitStorageEvent<'DELETE'>({ key });
		} else {
			this.emitStorageEvent<'SAVE'>({ key, data: newValue });
		}
	};
}
